import { Injectable, inject } from '@angular/core';
import { Observable, shareReplay, tap } from 'rxjs';
import { ApiService } from './api.service';
import { SaveWellDto } from '@models/save-well-dto.interface';
import { Well, WellSummary } from '@models/well.interface';

@Injectable({ providedIn: 'root' })
export class WellsService {
  private readonly apiService = inject(ApiService);
  private listCache$?: Observable<WellSummary[]>;
  private readonly detailCache = new Map<number, Observable<Well>>();

  getList(forceRefresh = false): Observable<WellSummary[]> {
    if (forceRefresh || !this.listCache$) {
      this.listCache$ = this.apiService
        .get('wells')
        .pipe(shareReplay(1));
    }

    return this.listCache$;
  }

  getById(id: number, forceRefresh = false): Observable<Well> {
    if (forceRefresh || !this.detailCache.has(id)) {
      const request$ = this.apiService
        .get(`wells/${id}`)
        .pipe(shareReplay(1));
      this.detailCache.set(id, request$);
    }

    return this.detailCache.get(id)!;
  }

  create(dto: SaveWellDto): Observable<Well> {
    return this.apiService
      .post('wells', dto)
      .pipe(tap(saved => this.invalidateCache(saved.id)));
  }

  update(id: number, dto: SaveWellDto): Observable<Well> {
    return this.apiService
      .put(`wells/${id}`, dto)
      .pipe(tap(saved => this.invalidateCache(saved.id)));
  }

  delete(id: number): Observable<void> {
    return this.apiService
      .delete(`wells/${id}`)
      .pipe(tap(() => this.invalidateCache(id)));
  }

  private invalidateCache(id?: number): void {
    this.listCache$ = undefined;
    if (id !== undefined) {
      this.detailCache.delete(id);
    }
  }
}

